/**
 * Login page — diseño "Expogreens con TOM" (handoff de Claude Design).
 * Panel verde con lockup TOM | EXPOGREENS + "Del cultivo al mundo", y
 * tarjeta blanca con el botón oliva. Demo: admin/admin123, vendedor/ventas123,
 * user/user123.
 */
import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { TomMark } from '@/components/ui/TomMark'

const GREEN = '#033021'
const OLIVE = '#C3DB5A'
const AMBER = '#FFC400'
const JOST = 'Jost, sans-serif'
const ARCHIVO = 'Archivo, sans-serif'

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('admin123')
  const [loading, setLoading] = useState(false)

  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/'
    return <Navigate to={from} replace />
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await login(username, password)
      toast.success('Sesión iniciada')
      const from = location.state?.from?.pathname || '/'
      navigate(from, { replace: true })
    } catch (err) {
      toast.error(err.message || 'Error al iniciar sesión')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = {
    width: '100%',
    border: '1px solid #E6EFE3',
    background: '#F4F6F4',
    borderRadius: 18,
    padding: '16px 18px',
    fontSize: 15,
    color: '#15301F',
    fontFamily: ARCHIVO,
    outline: 'none',
  }
  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: 700,
    color: '#15301F',
    marginBottom: 10,
    fontFamily: ARCHIVO,
  }
  const Dot = () => (
    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: GREEN, flex: 'none' }} />
  )

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexWrap: 'wrap', fontFamily: ARCHIVO }}>
      {/* ── Panel verde (marca) ── */}
      <div
        style={{
          flex: 1,
          minWidth: 340,
          background: GREEN,
          color: '#fff',
          padding: '38px 44px',
          display: 'flex',
          flexDirection: 'column',
        }}
        className="hidden md:flex"
      >
        {/* lockup */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <TomMark fontSize={28} dot={OLIVE} />
            <span style={{ width: 1.5, height: 28, background: 'rgba(255,255,255,.32)', flex: 'none' }} />
            <span style={{ fontFamily: JOST, fontWeight: 700, color: OLIVE, fontSize: 25, letterSpacing: '.02em', lineHeight: 1 }}>
              EXPOGREENS
            </span>
          </div>
          <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', marginTop: 7, paddingLeft: 2 }}>
            Gestión de Exportación de Follajes
          </div>
        </div>

        {/* headline */}
        <div style={{ margin: 'auto 0' }}>
          <h2 style={{ fontFamily: JOST, fontWeight: 700, fontSize: 72, lineHeight: 1.0, margin: 0, color: '#fff', letterSpacing: '-0.01em' }}>
            Del cultivo<br />al mundo.
          </h2>
          <p style={{ fontSize: 21, lineHeight: 1.55, color: 'rgba(255,255,255,.78)', maxWidth: 440, margin: '26px 0 0' }}>
            Gestiona ventas, compras, inventario y devoluciones desde un mismo panel.
          </p>
        </div>

        {/* footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>funciona con</span>
            <TomMark fontSize={18} dot={AMBER} />
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.4)' }}>© {new Date().getFullYear()} Expogreens</div>
        </div>
      </div>

      {/* ── Panel claro (form) ── */}
      <div
        style={{
          flex: 1,
          minWidth: 340,
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '44px 40px',
        }}
      >
        <form
          onSubmit={onSubmit}
          style={{
            background: '#fff',
            border: '1px solid #E6EFE3',
            borderRadius: 30,
            boxShadow: '0 18px 44px rgba(3,48,33,.10)',
            padding: '40px 38px',
            width: 420,
            maxWidth: '100%',
          }}
        >
          <div style={{ fontFamily: JOST, fontWeight: 700, fontSize: 30, color: '#15301F', lineHeight: 1 }}>
            Iniciar sesión
          </div>
          <div style={{ fontSize: 14.5, color: '#6a7a70', margin: '10px 0 28px' }}>
            Ingresa tus credenciales para continuar
          </div>

          <div style={labelStyle}><Dot />Usuario</div>
          <input
            style={{ ...inputStyle, marginBottom: 20 }}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />

          <div style={labelStyle}><Dot />Contraseña</div>
          <input
            type="password"
            style={{ ...inputStyle, marginBottom: 26 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: GREEN,
              color: '#fff',
              textAlign: 'center',
              fontFamily: JOST,
              fontWeight: 600,
              fontSize: 16,
              padding: 16,
              borderRadius: 999,
              border: 'none',
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 8px 20px rgba(3,48,33,.22)',
            }}
          >
            {loading ? 'Ingresando…' : '→  Iniciar sesión'}
          </button>

          <div style={{ marginTop: 26, borderTop: '1px solid #ececec', paddingTop: 18 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#5a6a60', marginBottom: 4 }}>Usuarios demo:</div>
            <div style={{ fontSize: 12.5, color: '#8a958c' }}>
              admin / admin123 · vendedor / ventas123 · user / user123
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
